import type { ReactNode } from 'react';

import MUICheckIcon from '@mui/icons-material/Check';

import type { SvgIconComponent } from '@mui/icons-material';
import { styled, SxProps, Theme } from '../styles';
import Box from '../Box';
import BulletPointArrowIcon from './BulletPointArrowIcon';
import Typography, { Variant } from '../Typography';
import markerIconSize from './constants/markerIconSize';

type MarkerType = 'arrow' | 'check' | 'dot' | SvgIconComponent;
export interface Props {
  children: string | ReactNode;
  marker?: MarkerType;
  variant?: Variant;
  sx?: SxProps<Theme>;
}

const Li = styled('li', {
  shouldForwardProp: (prop) => !['marker'].includes(prop as string),
})<{ marker: MarkerType }>(({ theme: { spacing }, marker }) => ({
  display: 'inline-flex',
  gap: marker === 'dot' ? 0 : 2,
  paddingRight: spacing(0.5),
  '&:not(:last-child)': {
    marginBottom: spacing(1),
  },
}));

const CheckIcon = styled(MUICheckIcon)(({ theme: { palette } }) => ({
  color: palette.icon.onMainHighlighted,
  width: markerIconSize,
  height: markerIconSize,
}));

const Dot = styled('span')({
  display: 'inline-block',
  '&::before': {
    display: 'block',
    content: "'•'",
    width: markerIconSize,
    height: markerIconSize,
    textAlign: 'center',
  },
});

const iconByType = {
  arrow: BulletPointArrowIcon,
  check: CheckIcon,
  dot: Dot,
};

const BulletPoint = ({ children, marker = 'dot', variant = 'body', sx }: Props) => {
  const isCustomIcon = typeof marker !== 'string';
  const Icon = isCustomIcon ? marker : iconByType[marker];

  return (
    <Li marker={marker} sx={sx}>
      <Box
        component={Icon}
        data-testid="data-test-bullet-point-icon"
        sx={{ marginRight: isCustomIcon ? 1 : 0 }}
      />
      {typeof children === 'string' ? (
        <Typography
          variant={variant}
          dangerouslySetInnerHTML={{
            __html: children as string,
          }}
        />
      ) : (
        children
      )}
    </Li>
  );
};

export default BulletPoint;
